<template>
  <v-dialog
    v-model="showModal"
    persistent
    max-width="900px"
    @keydown.enter.prevent="submit()"
    @keydown.esc.prevent="cancel()"
  >
    <el-overlay-loading :loading="pageloading" />
    <v-card class="modalCard">
      <v-card-title class="modalTitle pt-2 pb-1 mb-2">
        <h2>{{ sarasasTitle }}</h2>
      </v-card-title>
      <v-card-text class="pt-2 pb-2">
        <v-card class="mb-2 pb-4">
          <v-card-text>
            <template v-if="savoTurtas">
              <v-row align="baseline" class="mb-0">
                <v-col cols="12" class="mb-2">
                  {{ $t('Pasirinkite objektus, kurių duomenis norite įkelti') }}
                </v-col>
              </v-row>
            </template>

            <template v-if="!savoTurtas">
              <v-tabs v-model="paieskaTab" class="mb-4">
                <v-tabs-slider color="primary"></v-tabs-slider>
                <v-tab
                  class="paieska-tab"
                  v-for="item in paieskaList"
                  :key="item.value"
                >
                  <h4>{{ item.label }}</h4>
                </v-tab>
              </v-tabs>

              <v-tabs-items v-model="paieskaTab">
                <v-tab-item key="objektas">
                  <v-row align="baseline" class="mb-0">
                    <v-col cols="12" md="8">
                      <el-text-field
                        :label="$t('Unikalus numeris')"
                        :tooltip="
                          `${ntrTooltipPrefix} Turtas. Unikalus numeris. Paieškai`
                        "
                        :readonly="isSearching"
                        autofocus
                        v-model="objektasSelect"
                      />
                    </v-col>

                    <v-col cols="12" md="4">
                      <el-button
                        primary
                        @click.native="getData()"
                        :loading="loading"
                        :disabled="isSearching || !canSearch"
                        >{{ $t('Ieškoti') }}</el-button
                      >
                      <el-button
                        @click.native="cancelSearch()"
                        :loading="loading"
                        :disabled="isSearching"
                        >{{ $t('Išvalyti') }}</el-button
                      >
                    </v-col>
                  </v-row>
                </v-tab-item>
                <v-tab-item key="adresas">
                  <v-row align="baseline" class="mb-0">
                    <v-col cols="12" md="8">
                      <el-adresas
                        v-model="adresasSelect"
                        return-object
                        :label="$t('Adresas')"
                        :tooltip="
                          `${ntrTooltipPrefix} Turtas. Adresas. Paieškai`
                        "
                        :counter="1000"
                        autofocus
                      />
                    </v-col>
                    <v-col cols="12" md="4" class="pb-2">
                      <el-button
                        primary
                        @click.native="getData()"
                        :loading="loading"
                        :disabled="isSearching || !canSearch"
                        >{{ $t('Ieškoti') }}</el-button
                      >
                      <el-button
                        @click.native="cancelSearch()"
                        :loading="loading"
                        :disabled="isSearching"
                        >{{ $t('Išvalyti') }}</el-button
                      >
                    </v-col>
                  </v-row>
                </v-tab-item>
              </v-tabs-items>
            </template>

            <template v-if="showFail">
              <v-row align="baseline" class="mb-0">
                <v-col cols="12">
                  <el-savitarna-alert dense outlined type="error">
                    {{ failMsg }}
                  </el-savitarna-alert>
                </v-col>
              </v-row>
            </template>

            <v-data-table
              :headers="filteredHeaders"
              :items="itemsList"
              :item-class="rowClass"
              :loading="tableLoading"
              :search="search"
              :custom-filter="customSearch"
              :footer-props="{
                showFirstLastPage: true,
                showCurrentPage: true,
                itemsPerPageText: this.$t('Rodyti po:'),
                itemsPerPageAllText: this.$t('visi'),
                itemsPerPageOptions: itemsPerPageOptions,
              }"
              :hide-default-footer="hideFooter"
              :items-per-page="pageSize"
              item-key="tur_identifikatorius"
            >
              <template #top>
                <v-toolbar flat>
                  <v-text-field
                    v-model="search"
                    prepend-icon="mdi-magnify"
                    label=""
                    single-line
                    hide-details
                  ></v-text-field>
                </v-toolbar>
              </template>
              <template #[`header.actions`]="{ header }">
                {{ header.text }}
                <el-checkbox
                  v-if="showHeaderCheck"
                  class="align-center justify-center mt-0"
                  :validate-key="`check-all`"
                  v-model="headerCheck"
                  @click.native.stop="checkAll()"
                />
              </template>
              <template v-slot:body="{ items }">
                <tbody>
                  <template v-for="(item, index) in items">
                    <tr
                      :key="`t-${index}-1`"
                      @mouseenter="mouseEnter(item.indexTeise)"
                      @mouseleave="mouseLeave()"
                    >
                      <td>
                        <div class="text-center mb-2">
                          <el-checkbox
                            v-if="item.canCheck"
                            :validate-key="item.validateKey"
                            :disabled="item.disabled"
                            v-model="pasirinktiTurtai"
                            :checkValue="item.checkValue"
                            :key="item.validateKey"
                          />
                        </div>
                      </td>
                      <td>
                        <div>{{ item.tur_identifikatorius }}</div>
                        <div class="text-gray mt-1">
                          {{ item.tur_ntr_adresas }}
                        </div>
                        <div>
                          {{ item.isnuomota_dalis }}
                        </div>
                      </td>
                      <template v-if="item.isFirst">
                        <td
                          :rowspan="item.teise.countTurtas"
                          :class="
                            forceHover(item.indexTeise) +
                              ` ${
                                item.teise.isLast
                                  ? 'table-no-bottom-border'
                                  : ''
                              }`
                          "
                        >
                          <div>{{ item.teise.tda_pavadinimas }}</div>
                          <div class="text-gray mt-1">
                            <div v-if="item.teise.tda_pradzia">
                              {{ $t('Galioja nuo:') }}
                              {{ item.teise.tda_pradzia }}
                            </div>
                            <div v-if="item.teise.tda_terminas">
                              {{ $t('Terminas:') }}
                              {{ item.teise.tda_terminas }}
                            </div>
                            <div v-if="item.teise.tda_dokumentas">
                              {{ item.teise.tda_dokumentas }}
                            </div>
                          </div>
                        </td>
                      </template>
                    </tr>
                  </template>
                </tbody>
              </template>

              <template #[`item.actions`]="{ item }">
                <div class="text-center mb-2">
                  <el-checkbox
                    v-if="item.canCheck"
                    :validate-key="`check-${item.tur_identifikatorius}`"
                    :disabled="item.disabled"
                    v-model="pasirinktiTurtai"
                    :checkValue="item.tur_identifikatorius"
                    :key="`check-${item.tur_identifikatorius}`"
                    @click.native.stop="checkItem(item)"
                  />
                </div>
              </template>
              <template slot="loading">
                {{ $t('Kraunasi...') }}
              </template>
              <template slot="no-results">
                {{ $t('Duomenų nėra') }}
              </template>
              <template slot="no-data">
                {{ $t('Duomenų nėra') }}
              </template>
              <template #[`footer.page-text`]="props">
                {{ $t('Rodoma') }} {{ props.pageStart }}-{{ props.pageStop }}
                {{ $t('iš') }}
                {{ props.itemsLength }}
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-card-text>
      <v-card-actions class="modalActions" elevation="5">
        <v-spacer></v-spacer>
        <el-button @click.native="cancelData()" :loading="loading">
          {{ $t('Uždaryti') }}
        </el-button>
        <el-button
          primary
          :disabled="!enableSubmit"
          :loading="loading"
          @click.native.stop="saveData()"
        >
          {{ $t('Įkelti') }}
        </el-button>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex'
import busenaNtr from '@/mixins/busenaNtr'
import rowClass from '@/mixins/rowClass'
import tooltipNtr from '@/mixins/tooltipNtr'
import { regNrMaskFilter } from '@/mixins/regNrMask'

export default {
  name: 'ModalNtrTeiseSarasas',
  props: {
    value: Boolean,
    savoTurtas: Boolean,
    teiseAutosave: Boolean,
  },
  components: {},
  mixins: [busenaNtr, rowClass, tooltipNtr],
  data() {
    return {
      pageloading: false,
      loading: false,
      tableLoading: false,
      adresasSelect: null,
      objektasSelect: null,
      showFail: false,
      failMsg: null,
      search: null,
      itemsPerPageOptions: [10, 50, -1],
      pageSize: 10,
      isSearching: false,
      teises: [],
      pasirinktiTurtai: [],
      headerCheck: false,
      regNrPlaceholder: '##/#########',
      regNrMask: regNrMaskFilter,
      activeRow: null,
      paieskaTab: null,
      paieskaList: [
        {
          label: 'Pagal unikalų numerį',
          value: 'objektas',
        },
        {
          label: 'Pagal adresą',
          value: 'adresas',
        },
      ],
    }
  },
  computed: {
    ...mapGetters(['prasymasID']),
    showModal: {
      get() {
        return this.value
      },
      set(value) {
        this.adresasSelect = null
        this.objektasSelect = null
        this.showFail = false
        this.$emit('input', value)
      },
    },
    canSearch() {
      return this.adresasSelect || this.objektasSelect || this.savoTurtas
    },

    headers() {
      return [
        {
          text: this.$t('Pasirinkti'),
          align: 'center',
          sortable: false,
          value: 'actions',
          width: 100,
        },
        {
          text: this.$t('Turto informacija'),
          align: 'start',
          value: 'turtas',
        },
        {
          text: this.$t('Teisės informacija'),
          align: 'start',
          value: 'teise',
          width: 300,
        },
      ]
    },
    filteredHeaders() {
      return this.headers.filter(item => {
        return !item.hide
      })
    },
    itemsList() {
      let turtasList = []
      this.teises.forEach((teise, indexTeise) => {
        const newTeise = {
          tda_pavadinimas: teise.tda_pavadinimas,
          tda_pradzia: teise.tda_pradzia,
          tda_terminas: teise.tda_terminas,
          tda_dokumentas: teise.tda_dokumentas,
          countTurtas: teise.turtai.length,
          isLast: indexTeise === this.teises.length - 1,
        }
        teise.turtai
          .sort((a, b) => {
            if (!a.enabled && b.enabled) return 1
            return 0
          })
          .forEach((turtas, indexTurtas) => {
            turtas.canCheck = turtas.enabled
            turtas.disabled = !turtas.enabled
            turtas.isFirst = indexTurtas === 0
            turtas.indexTeise = indexTeise
            turtas.teise = newTeise
            turtas.checkValue = { obj_id: turtas.obj_id, tei_id: turtas.tei_id }
            turtas.validateKey = `check-${turtas.obj_id}-${turtas.tei_id}`

            turtasList.push({
              ...turtas,
            })
          })
      })

      return turtasList
    },
    hideFooter() {
      return this.itemsList.length <= 10
    },
    enableSubmit() {
      return this.pasirinktiTurtai.length > 0
    },
    showHeaderCheck() {
      return !this.isPrasymasSeima
    },
    sarasasTitle() {
      // if (this.isAdresas) {
      //   return this.$t('Nekilnojamojo turto paieška pagal adresą')
      // }
      return this.$t('Įkelti nekilnojamąjį turtą')
    },
    isAdresas() {
      return this.paieskaTab === 1
    },
    isObjektas() {
      return this.paieskaTab === 0
    },
  },
  watch: {
    showModal(val) {
      if (val) {
        if (this.savoTurtas) {
          this.getData()
        }
      }
    },
    paieskaTab(val) {
      this.showError = false
      this.adresasSelect = null
      this.objektasSelect = null
      this.resetSearch()
    },
    teiseAutosave(val) {
      if (val) {
        this.getData()
      }
    },
  },
  methods: {
    getData() {
      if (!this.canSearch) {
        return
      }
      this.loading = true
      this.tableLoading = true
      this.isSearching = true
      this.showFail = false
      this.resetSearch()

      const data = {
        code: this.isAdresas ? parseInt(this.adresasSelect.code) : null,
        tur_identifikatorius: this.isObjektas ? this.objektasSelect : null,
        savo_turtas: this.savoTurtas,
      }

      this.$axios
        .post(
          `ntrpaslaugos/prasymas/${this.prasymasID}/turtas/paieska-subnuomos`,
          data
        )
        .then(res => {
          this.teises = res.data.turtai
          this.checkAutoSave()
        })
        .catch(err => {
          this.$emit('updateTurtasList', err.response.data.message)
          this.closeModal()
        })
        .finally(() => {
          this.tableLoading = false
          this.loading = false
          this.isSearching = false
        })
    },
    checkAutoSave() {
      if (this.savoTurtas && this.teises.length === 1) {
        this.pasirinktiTurtai = []
        this.itemsList.forEach(item => {
          this.pasirinktiTurtai.push(item.checkValue)
        })
        this.saveData()
        return
      }
      this.showModal = true
    },
    saveData() {
      this.loading = true

      const data = {
        turtai: this.pasirinktiTurtai,
      }
      this.$axios
        .post(
          `ntrpaslaugos/prasymas/${this.prasymasID}/turtas/paieska-subnuomos-saugoti`,
          data
        )
        .then(res => {
          window._VMA.$emit('DATA_OK', res.data.message)
          this.$emit('updateTurtasList')
          this.closeModal()
        })
        .catch(err => {
          this.$emit('updateTurtasList', err.response.data.message)
          this.closeModal()
        })
        .finally(() => {
          this.loading = false
        })
    },
    checkAll() {
      this.pasirinktiTurtai = []
      if (this.headerCheck) {
        this.itemsList.forEach(item => {
          if (
            (!this.search || this.customSearch(null, this.search, item)) &&
            item.canCheck
          ) {
            this.pasirinktiTurtai.push(item.checkValue)
          }
        })
      }
    },

    cancelData() {
      this.closeModal()
    },
    closeModal() {
      this.$emit('cancelTeiseSarasas')
      this.resetSearch()
      this.paieskaTab = null
      this.showModal = false
    },
    submit() {
      this.getData()
    },
    cancel() {
      this.closeModal()
    },
    cancelSearch() {
      this.adresasSelect = null
      this.objektasSelect = null
      this.showFail = false
      this.resetSearch()
    },
    resetSearch() {
      this.headerCheck = false
      this.teises = []
      this.pasirinktiTurtai = []
    },
    getActiveClass(item) {
      if (item.disabled) {
        return 'row-disabled'
      }
      return ''
    },
    customSearch(value, search, item) {
      return Object.values(item).some(
        v =>
          v &&
          v
            .toString()
            .toLowerCase()
            .includes(search.toLowerCase())
      )
    },
    mouseEnter(i) {
      this.activeRow = i
    },
    mouseLeave() {
      this.activeRow = null
    },
    forceHover(i) {
      if (this.activeRow === i) {
        return 'force-hover'
      }
      return ''
    },
  },
}
</script>
<style lang="scss" scoped>
.modalCard {
  background: #f5f5f5 !important;
}
.modalTitle {
  background: #f5f5f5 !important;
  position: sticky;
  top: 0;
  z-index: 2;
  background: none;
  border-bottom: thin solid rgba(0, 0, 0, 0.12);
}
.modalActions {
  position: sticky;
  bottom: 0;
  z-index: 1;
}
.table-no-bottom-border {
  border-bottom: none !important;
}
.force-hover {
  background: #eee;
}
</style>
